import React from "react";
import styled, { keyframes } from "styled-components";
import HighlightPill from "../../components/HighlightPill";
import {
  TitleSection,
  SubtitleSection,
  RDVButton,
  Inner,
} from "../../components/commonWidgets";
import { FiSearch, FiClock, FiShield } from "react-icons/fi";
import { useState, useEffect } from "react";
import theme from "../../theme";
import AnimateOnScroll from "../../components/AnimateOnScroll";
import HighlightTitle from "../../components/HighlightTitle";

const Section = styled.section`
  /*background-color: #ffffff;*/
  width: 100%;
  padding: 2rem 0;
`;

const Container = styled(Inner)`
  display: flex;
  flex-direction: column;
  align-items: center;
  margin: auto;
  max-width: 800px;
  text-align: center;
  padding: 0 1rem;
`;

const CardsContainer = styled.div`
  display: flex;
  justify-content: center;
  flex-wrap: nowrap;
  gap: 2rem;
  margin-top: 2rem;

  ${theme.breakpoints.down("sm")} {
    flex-direction: column;
    align-items: center;
    gap: 1.5rem;
  }
`;

const Card = styled.div`
  background-color: var(--my-color3);
  color: var(--my-color3);
  width: 160px;
  height: 160px;
  border-radius: 50%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  box-shadow: 0 8px 18px rgba(0, 0, 0, 0.1);

  h3 {
    font-size: 1.5rem;
    margin: 0.2rem 0 0;
    color: var(--my-color5);
    font-weight: bold;
  }

  p {
    margin: 0.2rem 0 0;
    font-size: 0.75rem;
    text-align: center;
    padding: 0 0.4rem;
    color: white;
    padding: 0 1rem;
    height: 3rem;
  }

  svg {
    font-size: 1.9rem;
    color: var(--my-color5);
  }
`;

// Simple counter hook to animate numbers
const useCounter = (end, duration = 2000) => {
  const [value, setValue] = useState(0);

  useEffect(() => {
    let start = 0;
    const increment = end / (duration / 30);
    const interval = setInterval(() => {
      start += increment;
      if (start >= end) {
        start = end;
        clearInterval(interval);
      }
      setValue(Math.round(start));
    }, 30);
    return () => clearInterval(interval);
  }, [end, duration]);

  return value;
};

const StatCard = ({ icon: Icon, endValue, suffix = "", description }) => {
  const count = useCounter(endValue);
  return (
    <Card>
      <Icon color="var(--my-color1)" />
      <h3>
        {count}
        <span style={{ fontSize: "0.7rem", color: "var(--my-color5)" }}>
          {suffix}
        </span>
      </h3>
      <p>{description}</p>
    </Card>
  );
};

const Intro = styled.p`
  font-size: 1.1rem;
  color: var(--my-color3);
  margin-top: 1.2rem;
  max-width: 600px;
`;

const ButtonWrapper = styled.div`
  margin-top: 2rem;
`;

const PresenceSection = () => {
  const slide = keyframes`from { left: -100%; } to { left: 0; }`;

  const Quote = styled.p`
    position: relative;
    font-size: 1.1rem;
    font-style: italic;
    color: var(--my-color3);
    margin: 0.8rem 0 0;
    text-align: center;
    overflow: visible;
    /* underline removed */
  `;

  const steps = [
    {
      icon: FiSearch,
      endValue: 87,
      suffix: "%",
      description: "vérifient votre site avant d’appeler",
    },
    {
      icon: FiClock,
      endValue: 30,
      suffix: "s",
      description: "pour décider de rester ou partir",
    },
    {
      icon: FiShield,
      endValue: 4,
      suffix: " piliers",
      description: "Résultat • Preuve • Promesse • Confiance",
    },
  ];

  return (
    <Section>
      <Container>
        <HighlightTitle
          text="Accélérateur de confiance"
          highlightWord="Accélérateur"
          bg="var(--my-color3)"
          rotateText={false}
          mainTextColor="var(--my-color3)"
          textColor="white"
        />
        <Quote>
          « Votre site n’est pas une carte de visite : c’est un{" "}
          <HighlightPill angle="-2deg" bg="var(--my-color4)" rotateText={true}>
            accélérateur de confiance
          </HighlightPill>{" "}
          »
        </Quote>

        <Intro>
          Une présence web performante n’a pas pour but d’attirer — elle
          convertit. Nos interfaces, vidéos et études de cas transforment les
          visiteurs déjà intéressés en clients convaincus.
        </Intro>
        <CardsContainer>
          <AnimateOnScroll type="fade">
            <StatCard
              icon={FiSearch}
              endValue={87}
              suffix="%"
              description="vérifient votre site avant d’appeler"
            />
          </AnimateOnScroll>
          <AnimateOnScroll type="fade">
            <StatCard
              icon={FiClock}
              endValue={30}
              suffix="s"
              description="pour décider de rester ou partir"
            />
          </AnimateOnScroll>
          <AnimateOnScroll type="fade">
            <StatCard
              icon={FiShield}
              endValue={4}
              suffix=" piliers"
              description="Résultat • Preuve • Promesse • Confiance"
            />
          </AnimateOnScroll>
        </CardsContainer>
        <ButtonWrapper>
          <RDVButton label="Obtenir un audit gratuit" />
        </ButtonWrapper>
      </Container>
    </Section>
  );
};

export default PresenceSection;
